import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { X, ArrowLeftRight, BadgeCheck, Loader2, LogOut, Pencil, Scissors, ArrowLeft, ShieldCheck, Eye, Pencil as PencilIcon } from 'lucide-react';
import { switchProfile, leaveFamily, getMyFamily, revokeShare, getShares } from '../../redux/familySlice';
import { getFileURL } from '../../utils/fileURL';
import SplitMemberFlow from './SplitMemberFlow';

// Pick which person on this login to act as.
//
// Switching re-issues the TOKEN server-side — it is not a screen-only change.
// If the client simply asked for another patient's data, anyone could ask for
// anyone's. The page reloads afterwards so every cached list, dashboard and
// appointment view is re-fetched as the new person; leaving stale data on
// screen after a switch is how someone books an appointment against the wrong
// medical record.
//
// ACTIONS SIT BEHIND AN EDIT TOGGLE, deliberately. Splitting an account cannot
// be undone from this screen, and this is a list people open constantly just to
// switch profile. Having a one-tap "split" beside every name meant the
// destructive action lived where the routine one does. Edit costs one tap and
// removes that whole class of accident.
//
// REMOVE IS GONE. It did the same job as Split — take someone out of the group
// — through a second code path with its own rules about who could use it. Two
// buttons for one outcome is two things to get wrong, so Split is the only way
// out now.
export default function SwitchProfile({ isOpen, onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { primary, members, activePatientId } = useSelector((s) => s.family || {});
    const [busy, setBusy] = useState(null);
    const [editing, setEditing] = useState(null);   // patientId whose actions are open
    const [splitFor, setSplitFor] = useState(null); // member being split
    const [leaveAsk, setLeaveAsk] = useState(false); // in-panel confirm, not an alert
    const [giveUpAsk, setGiveUpAsk] = useState(null); // patientId whose access is being handed back

    if (!isOpen) return null;

    const people = [primary, ...(members || [])].filter(Boolean);
    const nameOf = (p) => [p?.name, p?.surname].filter(Boolean).join(' ').trim();

    const choose = async (patientId) => {
        if (patientId === activePatientId) { onClose(); return; }
        setBusy(patientId);
        try {
            await dispatch(switchProfile(patientId)).unwrap();
            // Hard reload: the whole app is now a different patient.
            window.location.reload();
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.switch_failed', 'Could not switch profile'));
            setBusy(null);
        }
    };

    // Handing back access to someone else's account. The share is looked up by
    // OWNER rather than kept on the row, because the switch list is about people
    // and the share id belongs to the sharing screen.
    const giveUp = async (person) => {
        setBusy(person.patientId);
        try {
            const res = await dispatch(getShares()).unwrap();
            const row = (res?.sharedWithMe || []).find((x) => x.patientId === person.patientId);
            if (!row) throw new Error('not found');
            await dispatch(revokeShare(row.shareId)).unwrap();
            toast.success(t('family.access_given_up', 'You no longer have access'));
            setGiveUpAsk(null);
            setTimeout(() => window.location.reload(), 900);
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.action_failed', 'That did not work'));
            setBusy(null);
        }
    };

    const leave = async () => {
        setLeaveAsk(false);
        setBusy('leave');
        try {
            const res = await dispatch(leaveFamily()).unwrap();
            toast.success(res?.message?.en || t('family.left', 'You have left the family account'));
            setTimeout(() => window.location.reload(), 900);
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.action_failed', 'That did not work'));
            setBusy(null);
        }
    };

    // Splitting the person you are CURRENTLY ACTING AS leaves the app signed in
    // as someone this login no longer reaches — every screen would 403. Go back
    // to the primary instead, and say why.
    const afterSplit = async (patientId) => {
        if (patientId !== activePatientId) return;
        toast.info(t('family.split_switched_back',
            'You were viewing that profile, so we have switched you back to the main account.'));
        try {
            await dispatch(switchProfile(primary.patientId)).unwrap();
        } catch { /* the reload below re-reads the session either way */ }
        setTimeout(() => window.location.reload(), 1200);
    };

    // RENDERED THROUGH A PORTAL, deliberately.
    //
    // This modal is opened from a header control, and its ancestors carry a CSS
    // `transform`. A transform makes an element the containing block for any
    // position:fixed descendant — so without a portal this "full screen" overlay
    // was trapped inside a narrow column and rendered squeezed and clipped.
    //
    // document.body sits outside that transform, so the overlay covers the page.
    return createPortal(
        <>
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4">
            <div data-testid="switch-profile-modal"
                 className={`w-full rounded-2xl bg-white shadow-xl transition-all ${splitFor ? 'max-w-md' : 'max-w-sm'}`}>
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                    <h3 className="flex min-w-0 items-center gap-2 font-bold text-[#1a365d]">
                        {splitFor ? (
                            <>
                                {/* Back returns to the list in place. Nothing was
                                    opened on top, so nothing has to be dismissed. */}
                                <button type="button" data-testid="split-back"
                                    onClick={() => setSplitFor(null)}
                                    className="cursor-pointer rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700">
                                    <ArrowLeft className="h-4 w-4" />
                                </button>
                                <ShieldCheck className="h-4 w-4 flex-shrink-0 text-[#4ABDBD]" />
                                <span className="truncate">
                                    {[splitFor.name, splitFor.surname].filter(Boolean).join(' ').trim()}
                                </span>
                            </>
                        ) : (
                            <>
                                <ArrowLeftRight className="h-4 w-4 text-[#4ABDBD]" />
                                {t('family.switch_profile', 'Switch profile')}
                            </>
                        )}
                    </h3>
                    <button type="button" onClick={onClose} className="cursor-pointer text-gray-400 hover:text-red-500">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {splitFor && (
                    <SplitMemberFlow
                        member={splitFor}
                        onDone={() => setSplitFor(null)}
                        onSplit={afterSplit}
                        onChanged={() => dispatch(getMyFamily())}
                    />
                )}

                {!splitFor && (
                <div className="flex max-h-[60vh] flex-col gap-1.5 overflow-y-auto px-3 py-3">
                    {people.map((p) => {
                        const isActive = p.patientId === activePatientId;
                        // The OWNER of this login — the one row that can never be
                        // lost, and so the only safe place to return to.
                        const isOwner = p.patientId === primary?.patientId;
                        // THREE KINDS OF ROW, not two. "Secondary" and "shared"
                        // are different relationships and offer different
                        // actions: a secondary can be SPLIT (they belong to this
                        // account), a shared account cannot — it is somebody
                        // else's, and all you can do is hand the access back.
                        const isSecondary = !isOwner && p.shareSource === 'secondary';
                        const isShared = !isOwner && !isSecondary;
                        const isOpen_ = editing === p.patientId;
                        return (
                          <div key={p.patientId} className="flex flex-col">
                            <div className={`flex items-center gap-2 rounded-xl border px-1 transition ${
                                isActive ? 'border-[#4ABDBD]/40 bg-[#4ABDBD]/10' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <button
                                    type="button"
                                    data-testid={`switch-to-${p.patientId}`}
                                    disabled={!!busy}
                                    onClick={() => choose(p.patientId)}
                                    className="flex flex-1 cursor-pointer items-center gap-3 px-2 py-2.5 text-left disabled:opacity-60"
                                >
                                    {p.image || p.imagePath ? (
                                        <img src={getFileURL(p.image || p.imagePath)} alt=""
                                            className="h-9 w-9 flex-shrink-0 rounded-full object-cover" />
                                    ) : (
                                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15 text-sm font-bold text-[#3AA8A8]">
                                            {(p.name || '?').charAt(0).toUpperCase()}
                                        </span>
                                    )}

                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-semibold text-gray-800">
                                            {nameOf(p)}
                                        </span>
                                        <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                                            <span>
                                                {isOwner
                                                    ? t('family.primary', 'Primary')
                                                    : isSecondary
                                                        ? t('family.secondary', 'Secondary')
                                                        // Not "Primary": what matters here is HOW you
                                                        // reach them, and this account is someone
                                                        // else's that they chose to share.
                                                        : t('family.shared', 'Shared')}
                                                {p.relationship ? ` · ${p.relationship}` : ''}
                                            </span>
                                            {/* WHAT YOU MAY DO, said on the row itself.
                                                Read and Read & Write look identical
                                                until you try something, and finding out
                                                by being refused is the worst way to
                                                learn it. Own account carries no badge —
                                                you always have full rights there. */}
                                            {!isOwner && p.permission === 'read' && (
                                                <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 py-px text-[9px] font-semibold text-gray-500">
                                                    <Eye className="h-2.5 w-2.5" />
                                                    {t('family.read_only', 'Read only')}
                                                </span>
                                            )}
                                            {!isOwner && p.permission === 'readwrite' && p.shareSource !== 'secondary' && (
                                                <span className="inline-flex items-center gap-0.5 rounded bg-[#4ABDBD]/15 px-1 py-px text-[9px] font-semibold text-[#3AA8A8]">
                                                    <PencilIcon className="h-2.5 w-2.5" />
                                                    {t('family.read_write', 'Read & write')}
                                                </span>
                                            )}
                                        </span>
                                    </span>

                                    {busy === p.patientId
                                        ? <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-[#4ABDBD]" />
                                        : p.hasLogin && <BadgeCheck className="h-4 w-4 flex-shrink-0 text-[#4ABDBD]"
                                                            title={t('family.has_login', 'Has their own sign-in')} />}
                                </button>

                                {/* NO EDIT ON THE PRIMARY. The primary is the
                                    account itself — splitting them would leave
                                    everyone else without an owner. To break up a
                                    family, split the members off first. */}
                                {/* A SHARED account is somebody else's. It cannot
                                    be split — that would be deciding something
                                    about their record — so the only action is to
                                    hand the access back. */}
                                {isShared && (
                                    <button
                                        type="button"
                                        data-testid={`give-up-${p.patientId}`}
                                        disabled={!!busy}
                                        onClick={() => setGiveUpAsk(giveUpAsk === p.patientId ? null : p.patientId)}
                                        className={`mr-1 flex-shrink-0 rounded-lg px-2 py-1 text-[10px] font-semibold transition cursor-pointer disabled:opacity-50 ${
                                            giveUpAsk === p.patientId
                                                ? 'bg-red-50 text-red-600'
                                                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                                    >
                                        {t('family.give_up', 'Give up')}
                                    </button>
                                )}

                                {isSecondary && p.canWrite && (
                                    <button
                                        type="button"
                                        data-testid={`edit-${p.patientId}`}
                                        disabled={!!busy}
                                        title={t('family.edit', 'Edit')}
                                        onClick={() => setEditing(isOpen_ ? null : p.patientId)}
                                        className={`mr-1 flex-shrink-0 rounded-lg p-2 transition cursor-pointer disabled:opacity-50 ${
                                            isOpen_ ? 'bg-[#4ABDBD]/20 text-[#3AA8A8]' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>

                            {/* Options appear UNDER the card they belong to, so
                                there is never a doubt about who they apply to. */}
                            {giveUpAsk === p.patientId && (
                                <div className="mb-1 mt-1 rounded-xl border border-red-200 bg-red-50 p-2.5">
                                    <p className="text-[11px] font-bold text-red-700">
                                        {t('family.give_up_title', 'Give up access to this account?')}
                                    </p>
                                    <p className="mt-1 text-[10px] leading-relaxed text-red-900">
                                        {t('family.give_up_body',
                                           'You will no longer be able to open it. Their records are unaffected, and they can share with you again at any time.')}
                                    </p>
                                    <div className="mt-2 flex gap-2">
                                        <button type="button" data-testid={`give-up-confirm-${p.patientId}`}
                                            disabled={!!busy}
                                            onClick={() => giveUp(p)}
                                            className="flex-1 cursor-pointer rounded-lg bg-red-600 py-1.5 text-[10px] font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                                            {busy === p.patientId
                                                ? <Loader2 className="mx-auto h-3 w-3 animate-spin" />
                                                : t('family.give_up', 'Give up')}
                                        </button>
                                        <button type="button" onClick={() => setGiveUpAsk(null)}
                                            className="flex-1 cursor-pointer rounded-lg border border-gray-200 bg-white py-1.5 text-[10px] font-semibold text-gray-600 hover:bg-gray-50">
                                            {t('common.cancel', 'Cancel')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {isOpen_ && isSecondary && (
                                <div className="mb-1 mt-1 flex flex-col gap-1 rounded-xl bg-gray-50 px-3 py-2">
                                    <button
                                        type="button"
                                        data-testid={`split-${p.patientId}`}
                                        disabled={!!busy}
                                        onClick={() => { setEditing(null); setSplitFor(p); }}
                                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] font-semibold text-[#3AA8A8] transition hover:bg-white disabled:opacity-50"
                                    >
                                        <Scissors className="h-3 w-3" />
                                        {t('family.split', 'Split into their own account')}
                                    </button>

                                    {/* You can always leave a family that can read
                                        your records — but only your own row. */}
                                    {isActive && !leaveAsk && (
                                        <button
                                            type="button"
                                            data-testid="leave-family"
                                            disabled={!!busy}
                                            onClick={() => setLeaveAsk(true)}
                                            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] font-semibold text-red-500 transition hover:bg-white disabled:opacity-50"
                                        >
                                            <LogOut className="h-3 w-3" />
                                            {t('family.leave', 'Leave this family')}
                                        </button>
                                    )}

                                    {isActive && leaveAsk && (
                                        <div className="rounded-lg border border-red-200 bg-red-50 p-2.5">
                                            <p className="text-[11px] font-bold text-red-700">
                                                {t('family.leave_title', 'Leave this family account?')}
                                            </p>
                                            <p className="mt-1 text-[10px] leading-relaxed text-red-900">
                                                {t('family.leave_body',
                                                   'The people on this account will no longer be able to see or manage your health records. Your records stay with you.')}
                                            </p>
                                            <div className="mt-2 flex gap-2">
                                                <button type="button" data-testid="leave-confirm"
                                                    disabled={!!busy} onClick={leave}
                                                    className="flex-1 cursor-pointer rounded-lg bg-red-600 py-1.5 text-[10px] font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                                                    {busy === 'leave'
                                                        ? <Loader2 className="mx-auto h-3 w-3 animate-spin" />
                                                        : t('family.leave_yes', 'Yes, leave')}
                                                </button>
                                                <button type="button" disabled={!!busy}
                                                    onClick={() => setLeaveAsk(false)}
                                                    className="flex-1 cursor-pointer rounded-lg border border-gray-200 bg-white py-1.5 text-[10px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                                                    {t('common.cancel', 'Cancel')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                          </div>
                        );
                    })}
                </div>
                )}
            </div>
        </div>
        </>,
        document.body
    );
}
